/**
 * SSE-Streaming-Hook für Chatbot (#1180).
 *
 * Nutzt fetch + ReadableStream statt EventSource, weil wir POST-Body senden
 * müssen (EventSource ist GET-only).
 *
 * Emittiert Events: init, text_delta, tool_use, tool_result, message_end,
 * usage, error, done.
 */
import { useCallback, useRef, useState } from 'react';

const API = '';

export function useChatStream() {
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);

  const stop = useCallback(() => {
    try { abortRef.current?.abort(); } catch (e) { /* noop */ }
    setStreaming(false);
  }, []);

  const send = useCallback(async ({ sessionId, message, attachments, onEvent }) => {
    if (streaming) return;
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
        body: JSON.stringify({ message, attachments: attachments || [] }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        onEvent?.({ type: 'error', message: text || `HTTP ${res.status}` });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = raw.split('\n').find(l => l.startsWith('data:'));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            onEvent?.(ev);
            if (ev.type === 'done' || ev.type === 'error') {
              // Stream is finished
            }
          } catch (e) {
            // Ignore JSON-parse errors (heartbeats etc.)
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        onEvent?.({ type: 'error', message: err.message });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [streaming]);

  return { send, stop, streaming };
}
